import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Grid,
  Stack,
  Divider,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  FileCopy as DuplicateIcon,
  Refresh as RefreshIcon,
  Groups as GroupsIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import grupoUsuarioService, { GrupoUsuario, PermissionCatalogResponse } from '../services/grupoUsuarioService';
import GrupoPermissoesDialog from '../components/forms/GrupoPermissoesDialog';
import SysKpiCard from '../components/common/SysKpiCard';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

interface DialogState {
  open: boolean;
  mode: 'create' | 'edit' | 'permissions';
  grupo: GrupoUsuario | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

const GrupoUsuarioPage: React.FC = () => {
  const theme = useTheme();
  const [grupos, setGrupos] = useState<GrupoUsuario[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedGrupo, setSelectedGrupo] = useState<GrupoUsuario | null>(null);
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    mode: 'create',
    grupo: null,
  });
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
  });
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Carregar grupos e catálogo
  const loadGrupos = useCallback(async () => {
    try {
      setLoading(true);
      const [gruposData, catalogData] = await Promise.all([
        grupoUsuarioService.list(),
        grupoUsuarioService.getCatalog(),
      ]);
      setGrupos(gruposData || []);
      setCatalog(catalogData || { permissions: {}, modules: {} });
    } catch (error: any) {
      console.error('Erro ao carregar grupos:', error);
      setGrupos([]);
      setCatalog({ permissions: {}, modules: {} });
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Erro ao carregar grupos',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGrupos();
  }, [loadGrupos]);

  const handleOpenDialog = (mode: 'create' | 'edit' | 'permissions', grupo?: GrupoUsuario) => {
    if (grupo) {
      setFormData({ nome: grupo.nome, descricao: grupo.descricao || '' });
      setDialogState({ open: true, mode, grupo });
    } else {
      setFormData({ nome: '', descricao: '' });
      setDialogState({ open: true, mode, grupo: null });
    }
  };

  const handleCloseDialog = () => {
    setDialogState({ open: false, mode: 'create', grupo: null });
    setFormData({ nome: '', descricao: '' });
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      setSnackbar({
        open: true,
        message: 'Nome do grupo é obrigatório',
        severity: 'error',
      });
      return;
    }

    try {
      if (dialogState.mode === 'create') {
        await grupoUsuarioService.create({
          nome: formData.nome,
          descricao: formData.descricao,
          permissoes: [],
        });
        setSnackbar({
          open: true,
          message: 'Grupo criado com sucesso',
          severity: 'success',
        });
      } else if (dialogState.mode === 'edit' && dialogState.grupo) {
        await grupoUsuarioService.update(dialogState.grupo.id, {
          nome: formData.nome,
          descricao: formData.descricao,
        });
        setSnackbar({
          open: true,
          message: 'Grupo atualizado com sucesso',
          severity: 'success',
        });
      }
      handleCloseDialog();
      await loadGrupos();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao salvar',
        severity: 'error',
      });
    }
  };

  const handleSavePermissions = async (permissoes: string[]) => {
    if (!dialogState.grupo) return;
    try {
      await grupoUsuarioService.updatePermissoes(
        dialogState.grupo.id,
        permissoes
      );
      setSnackbar({
        open: true,
        message: 'Permissões atualizadas com sucesso',
        severity: 'success',
      });
      handleCloseDialog();
      await loadGrupos();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao salvar permissões',
        severity: 'error',
      });
    }
  };

  const handleDuplicate = async () => {
    if (!selectedGrupo) return;
    try {
      const novoNome = `${selectedGrupo.nome} (cópia)`;
      await grupoUsuarioService.duplicate(selectedGrupo.id, {
        nome: novoNome,
      });
      setSnackbar({
        open: true,
        message: 'Grupo duplicado com sucesso',
        severity: 'success',
      });
      setMenuAnchor(null);
      await loadGrupos();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao duplicar',
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedGrupo) return;
    if (!window.confirm(`Tem certeza que deseja excluir o grupo "${selectedGrupo.nome}"?`)) return;

    try {
      await grupoUsuarioService.delete(selectedGrupo.id);
      setSnackbar({
        open: true,
        message: 'Grupo excluído com sucesso',
        severity: 'success',
      });
      setMenuAnchor(null);
      await loadGrupos();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao excluir',
        severity: 'error',
      });
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedGrupos = (grupos || []).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box sx={{ ...pageShellSx, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 1 }}>
        <CircularProgress size={32} />
        <Typography variant="caption" color="text.secondary">Carregando grupos...</Typography>
      </Box>
    );
  }

  const totalPermissoes = grupos.reduce((acc, g) => acc + (g.permissoes?.length || 0), 0);

  return (
    <Box sx={pageShellSx}>
      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'secondary')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <GroupsIcon sx={{ fontSize: 120, color: theme.palette.secondary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}` }}>
              <GroupsIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Grupos de Usuários</Typography>
              <Typography variant="caption" color="text.secondary">Gerencie permissões e acesso por grupo</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadGrupos}>Atualizar</Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog('create')}>Novo Grupo</Button>
          </Stack>
        </Stack>
      </Paper>

      {grupos.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Total de Grupos" valor={grupos.length} icone={<GroupsIcon sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Permissões" valor={totalPermissoes} subtitulo="total atribuídas" icone={<SecurityIcon sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} /></Grid>
          <Grid item xs={6} md={4}><SysKpiCard titulo="Média por Grupo" valor={grupos.length ? Math.round(totalPermissoes / grupos.length) : 0} icone={<SecurityIcon sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Grupos</Typography>
          <Chip size="small" label={`${grupos.length} grupo${grupos.length !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>

        {grupos.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <GroupsIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhum grupo cadastrado</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={rankingTableHeadRowSx(theme)}>
              <TableCell>Nome</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell align="center">Permissões</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(paginatedGrupos || []).map((grupo) => (
              <TableRow key={grupo.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {grupo.nome}
                  </Typography>
                  {grupo.descricao && (
                    <Typography variant="caption" color="text.secondary">
                      {grupo.descricao}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={grupo.slug} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption">
                    {grupo.permissoes?.length || 0} permissões
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setSelectedGrupo(grupo);
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Box sx={tablePaginationWrapperSx(theme)}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          size="small"
          count={grupos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas:"
          sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
        />
      </Box>
          </>
        )}
      </Paper>

      {/* Menu de ações */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            handleOpenDialog('edit', selectedGrupo!);
            setMenuAnchor(null);
          }}
        >
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Editar
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenDialog('permissions', selectedGrupo!);
            setMenuAnchor(null);
          }}
        >
          <Chip label="Permissões" size="small" sx={{ mr: 1 }} />
          Gerenciar Permissões
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <DuplicateIcon sx={{ mr: 1, fontSize: 20 }} />
          Duplicar
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Excluir
        </MenuItem>
      </Menu>

      {/* Dialog para criar/editar */}
      <Dialog open={dialogState.open && dialogState.mode !== 'permissions'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogState.mode === 'create' ? 'Novo Grupo' : 'Editar Grupo'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Nome"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Descrição"
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <GrupoPermissoesDialog
        open={dialogState.open && dialogState.mode === 'permissions'}
        onClose={handleCloseDialog}
        grupo={dialogState.grupo}
        catalog={catalog}
        onSave={handleSavePermissions}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GrupoUsuarioPage;
